/**
 * PDF Generator
 *
 * Client-side report generation for the three primary record types in the
 * platform: Inspections, Work Orders, and Project Work Orders. Uses jspdf
 * to build a multi-section document with the brand header, key-value
 * detail sections, and a Photos section when applicable. The completed
 * file is downloaded directly via `doc.save(filename)` — no backend
 * round-trip, no server-rendered template.
 *
 * Two flavors per entity:
 *  - Detail reports (generateXxxPdf) — called from the detail pages,
 *    receive a single fully-hydrated record and render a head-to-toe
 *    profile of that record.
 *  - List reports (generateXxxListPdf) — called from the list pages,
 *    receive the currently filtered array of rows and emit a tabular
 *    summary one record per line for printing / sharing.
 *
 * All entry points share a small internal layout helper (PdfLayout) to
 * keep the visual style consistent across both flavors.
 */

import jsPDF from 'jspdf';
import type { Inspection } from '../services/inspectionService';
import type { WorkOrder } from '../services/workOrderService';
import type { ProjectWorkOrder } from '../services/projectWorkOrderService';
import { formatDate, formatDateTime, formatDay } from './formatDate';
import { INSPECTION_RATINGS, ratingLabel } from './inspectionRating';
import { formatPersonName } from './formatLabel';

/* ============================================
 * CONSTANTS
 * ============================================ */

/** GBrands orange — matches `--brand-primary` in brand.css. */
const BRAND_RGB: [number, number, number] = [249, 85, 14];

/** Body text grey — slightly softer than pure black for readability. */
const BODY_RGB: [number, number, number] = [40, 40, 40];

/** Muted label colour for section headings and key labels. */
const MUTED_RGB: [number, number, number] = [110, 110, 110];

/** Page-edge margin in millimetres (jspdf default unit). */
const MARGIN_X = 14;
const MARGIN_TOP = 18;
const MARGIN_BOTTOM = 18;

/* ============================================
 * LAYOUT HELPER
 * ============================================ */

/**
 * Stateful drawing cursor that wraps a jsPDF document and tracks the
 * current y-offset. The detail rendering functions push sections through
 * `section()` / `kv()` and the helper handles wrapping, page breaks, and
 * heading typography so each entity report has a uniform look.
 */
class PdfLayout {
	doc: jsPDF;
	y: number;
	pageHeight: number;
	pageWidth: number;
	contentWidth: number;

	constructor(doc: jsPDF) {
		this.doc = doc;
		this.pageHeight = doc.internal.pageSize.getHeight();
		this.pageWidth = doc.internal.pageSize.getWidth();
		this.contentWidth = this.pageWidth - MARGIN_X * 2;
		this.y = MARGIN_TOP;
	}

	/** Ensures `space` mm fits on the current page; otherwise starts a new one. */
	ensure(space: number) {
		if (this.y + space > this.pageHeight - MARGIN_BOTTOM) {
			this.doc.addPage();
			this.y = MARGIN_TOP;
		}
	}

	/** Renders the report header — brand bar + title + subtitle. */
	header(title: string, subtitle: string) {
		// Brand orange bar across the top edge of the page
		this.doc.setFillColor(...BRAND_RGB);
		this.doc.rect(0, 0, this.pageWidth, 10, 'F');

		this.y = MARGIN_TOP;
		this.doc.setTextColor(...BRAND_RGB);
		this.doc.setFont('helvetica', 'bold');
		this.doc.setFontSize(18);
		this.doc.text(title, MARGIN_X, this.y);

		this.y += 7;
		this.doc.setTextColor(...MUTED_RGB);
		this.doc.setFont('helvetica', 'normal');
		this.doc.setFontSize(10);
		this.doc.text(subtitle, MARGIN_X, this.y);

		this.y += 4;
		this.doc.setDrawColor(...BRAND_RGB);
		this.doc.setLineWidth(0.4);
		this.doc.line(MARGIN_X, this.y, this.pageWidth - MARGIN_X, this.y);
		this.y += 8;
	}

	/** Renders a section heading (e.g. "Customer & Building"). */
	section(heading: string) {
		this.ensure(12);
		this.doc.setTextColor(...BRAND_RGB);
		this.doc.setFont('helvetica', 'bold');
		this.doc.setFontSize(12);
		this.doc.text(heading, MARGIN_X, this.y);
		this.y += 5;
		// Thin separator under the heading
		this.doc.setDrawColor(...BRAND_RGB);
		this.doc.setLineWidth(0.2);
		this.doc.line(MARGIN_X, this.y, this.pageWidth - MARGIN_X, this.y);
		this.y += 4;
	}

	/**
	 * Renders a single label/value row. Long values wrap; an em-dash is
	 * shown for empty values so the section reads cleanly.
	 *
	 * Labels are wrapped to the label column width too — long labels
	 * like "PRIMARY ASSIGNEE (CORRECTIONS)" used to overrun the value
	 * column and visually collide with the value text. The row's total
	 * height is the taller of the two columns so neither stack clips.
	 */
	kv(label: string, value: string | number | null | undefined) {
		const display = value === null || value === undefined || value === '' ? '—' : String(value);
		const labelWidth = 42;
		// Reserve a small gutter between label + value so wrapped labels
		// don't visually butt up against the value column when they hit
		// the column edge exactly.
		const gutter = 3;
		const valueWidth = this.contentWidth - labelWidth - gutter;

		// Measure the label in its eventual font (9pt bold helvetica)
		// and wrap it into the label column. Leave a 2mm safety margin
		// inside the column so descenders / italic tails don't kiss the
		// value column.
		this.doc.setFont('helvetica', 'bold');
		this.doc.setFontSize(9);
		this.doc.setTextColor(...MUTED_RGB);
		const labelLines = this.doc.splitTextToSize(
			label.toUpperCase(),
			labelWidth - 2,
		) as string[];

		// Measure the value in its eventual font (10pt regular).
		this.doc.setFont('helvetica', 'normal');
		this.doc.setFontSize(10);
		this.doc.setTextColor(...BODY_RGB);
		const valueLines = this.doc.splitTextToSize(display, valueWidth) as string[];

		// Row height is the taller of the two columns so wrapped labels
		// + single-line values (or vice-versa) don't overlap the next
		// row. Slightly different line heights match the per-column
		// font sizes (label 9pt → ~4.0mm, value 10pt → ~4.6mm).
		const labelHeight = labelLines.length * 4.0;
		const valueHeight = valueLines.length * 4.6;
		const rowHeight = Math.max(6, labelHeight, valueHeight);
		this.ensure(rowHeight);

		// Label (muted, semibold)
		this.doc.setFont('helvetica', 'bold');
		this.doc.setFontSize(9);
		this.doc.setTextColor(...MUTED_RGB);
		this.doc.text(labelLines, MARGIN_X, this.y);

		// Value (body, regular)
		this.doc.setFont('helvetica', 'normal');
		this.doc.setFontSize(10);
		this.doc.setTextColor(...BODY_RGB);
		this.doc.text(valueLines, MARGIN_X + labelWidth, this.y);

		this.y += rowHeight;
	}

	/** Adds a blank space between sections. */
	spacer(size = 4) {
		this.y += size;
	}

	/**
	 * Renders a tabular block — a single header row followed by data rows.
	 * Columns are sized via fractional weights (e.g. [2, 3, 1] for a 6-unit
	 * total). Cells wrap; long rows that overflow the page push to a new
	 * page with the header re-drawn so the table stays readable.
	 */
	table(headers: string[], rows: string[][], weights?: number[]) {
		const cols = headers.length;
		const w = weights && weights.length === cols ? weights : Array(cols).fill(1);
		const totalWeight = w.reduce((s, n) => s + n, 0);
		const colWidths = w.map((weight) => (this.contentWidth * weight) / totalWeight);

		const lineHeight = 4.2;
		const rowPadding = 2;

		const drawHeader = () => {
			this.ensure(8);
			this.doc.setFillColor(245, 245, 245);
			this.doc.rect(MARGIN_X, this.y - 4, this.contentWidth, 7, 'F');
			this.doc.setFont('helvetica', 'bold');
			this.doc.setFontSize(8);
			this.doc.setTextColor(...MUTED_RGB);
			let x = MARGIN_X + 2;
			headers.forEach((h, i) => {
				this.doc.text(h.toUpperCase(), x, this.y);
				x += colWidths[i];
			});
			this.y += 5;
			this.doc.setDrawColor(220, 220, 220);
			this.doc.setLineWidth(0.2);
			this.doc.line(MARGIN_X, this.y, this.pageWidth - MARGIN_X, this.y);
			this.y += 3;
		};

		drawHeader();

		this.doc.setFont('helvetica', 'normal');
		this.doc.setFontSize(9);
		this.doc.setTextColor(...BODY_RGB);

		rows.forEach((row) => {
			// Pre-wrap each cell so we know the tallest cell in the row
			const wrapped = row.map((cell, i) =>
				this.doc.splitTextToSize(cell ?? '—', colWidths[i] - 4) as string[],
			);
			const rowLines = Math.max(...wrapped.map((lines) => lines.length));
			const rowHeight = rowLines * lineHeight + rowPadding;

			// If the row won't fit, start a new page and redraw the header
			if (this.y + rowHeight > this.pageHeight - MARGIN_BOTTOM) {
				this.doc.addPage();
				this.y = MARGIN_TOP;
				drawHeader();
				this.doc.setFont('helvetica', 'normal');
				this.doc.setFontSize(9);
				this.doc.setTextColor(...BODY_RGB);
			}

			let x = MARGIN_X + 2;
			wrapped.forEach((lines, i) => {
				this.doc.text(lines, x, this.y);
				x += colWidths[i];
			});
			this.y += rowHeight;

			// Faint row separator
			this.doc.setDrawColor(235, 235, 235);
			this.doc.setLineWidth(0.1);
			this.doc.line(MARGIN_X, this.y - 1, this.pageWidth - MARGIN_X, this.y - 1);
		});
	}

	/**
	 * Renders a grid of captioned photos, two per row.
	 *
	 * Each cell is the image, its title, and its description. A cell that
	 * won't fit on the remaining page starts a new page rather than being
	 * clipped. Images that failed to load are skipped by the caller, so
	 * everything reaching here is drawable.
	 */
	photoGrid(photos: Array<{ dataUrl: string; format: string; width: number; height: number; title: string; caption: string }>) {
		const columns = 2;
		const gutter = 6;
		const cellWidth = (this.contentWidth - gutter * (columns - 1)) / columns;
		// Cap the image box so a portrait photo can't take a whole page.
		const maxImageHeight = 55;

		for (let i = 0; i < photos.length; i += columns) {
			const row = photos.slice(i, i + columns);

			// Scale each image to fit the cell, then size the row to the
			// tallest one plus room for its two lines of text.
			const scaled = row.map((photo) => {
				const ratio = photo.height > 0 ? photo.height / photo.width : 0.75;
				const height = Math.min(cellWidth * ratio, maxImageHeight);
				const width = ratio > 0 ? height / ratio : cellWidth;
				return { ...photo, drawWidth: Math.min(width, cellWidth), drawHeight: height };
			});

			const imageHeight = Math.max(...scaled.map((p) => p.drawHeight));
			const textHeight = 10;
			this.ensure(imageHeight + textHeight + 4);

			scaled.forEach((photo, column) => {
				const x = MARGIN_X + column * (cellWidth + gutter);
				try {
					this.doc.addImage(photo.dataUrl, photo.format, x, this.y, photo.drawWidth, photo.drawHeight);
				} catch {
					// A single unreadable image must not abort the whole report.
					return;
				}

				let textY = this.y + imageHeight + 4;
				if (photo.title) {
					this.doc.setFont('helvetica', 'bold');
					this.doc.setFontSize(8);
					this.doc.setTextColor(...BODY_RGB);
					this.doc.text(this.doc.splitTextToSize(photo.title, cellWidth) as string[], x, textY);
					textY += 3.5;
				}
				if (photo.caption) {
					this.doc.setFont('helvetica', 'normal');
					this.doc.setFontSize(8);
					this.doc.setTextColor(...MUTED_RGB);
					// One line only — the full text is in the results table.
					const lines = this.doc.splitTextToSize(photo.caption, cellWidth) as string[];
					this.doc.text(lines.slice(0, 1), x, textY);
				}
			});

			this.y += imageHeight + textHeight + 4;
		}
	}

	/** Footer with generation timestamp + page numbers, drawn at save() time. */
	footer() {
		const total = this.doc.getNumberOfPages();
		for (let i = 1; i <= total; i++) {
			this.doc.setPage(i);
			this.doc.setFont('helvetica', 'normal');
			this.doc.setFontSize(8);
			this.doc.setTextColor(...MUTED_RGB);
			const stamp = `Generated ${formatDateTime(new Date().toISOString())}`;
			this.doc.text(stamp, MARGIN_X, this.pageHeight - 8);
			const pageLabel = `Page ${i} of ${total}`;
			const labelWidth = this.doc.getTextWidth(pageLabel);
			this.doc.text(pageLabel, this.pageWidth - MARGIN_X - labelWidth, this.pageHeight - 8);
		}
	}
}

/* ============================================
 * SHARED FIELD HELPERS
 * ============================================ */

/**
 * Title-cases a snake_case or kebab-case enum string (e.g. "in_progress"
 * → "In Progress"). Inspection / WO statuses round-trip through here.
 */
function formatLabel(value: string | null | undefined): string {
	if (!value) return '—';
	return value
		.replace(/[_-]/g, ' ')
		.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Joins multi-line address parts with em-dashes for readable single-line output. */
function formatAddress(parts: Array<string | null | undefined>): string {
	const filled = parts.filter((p) => p && String(p).trim() !== '');
	return filled.length ? filled.join(' • ') : '—';
}

/** Formats a number-or-string hour count, hiding trailing zeros. */
function formatHours(value: number | string | null | undefined): string {
	if (value === null || value === undefined || value === '') return '—';
	const num = Number(value);
	if (!Number.isFinite(num)) return String(value);
	return `${num} hr${num === 1 ? '' : 's'}`;
}

/** Slugifies a string for use in a filename (keeps the download tidy). */
function slugify(value: string): string {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 60) || 'report';
}

/* ============================================
 * INSPECTION REPORT
 * ============================================ */

/* ============================================
 * PHOTO EMBEDDING
 * ============================================ */

/** A photo loaded and ready for jsPDF. */
interface EmbeddablePhoto {
	dataUrl: string;
	/** jsPDF format token — 'JPEG' or 'PNG'. */
	format: string;
	width: number;
	height: number;
	title: string;
	caption: string;
}

/**
 * Load one photo URL into a data URL jsPDF can embed, downscaling it on the
 * way through.
 *
 * Field photos are multi-megapixel. Embedding them at full size produces a
 * report too large to email, which defeats the point of generating it — so
 * each image is drawn onto a canvas capped at MAX_EMBED_PX and re-encoded as
 * JPEG.
 *
 * Resolves to null on any failure (network, CORS, a decode error). One
 * unreadable photo must never take the whole report down with it.
 */
const MAX_EMBED_PX = 900;

async function loadPhotoForEmbed(
	url: string,
	title: string,
	caption: string,
): Promise<EmbeddablePhoto | null> {
	return new Promise((resolve) => {
		const image = new Image();
		// Required for a canvas read of a cross-origin image (the API host, or
		// blob storage, differs from the app origin in every deployment).
		image.crossOrigin = 'anonymous';

		image.onload = () => {
			try {
				const scale = Math.min(1, MAX_EMBED_PX / Math.max(image.width, image.height));
				const canvas = document.createElement('canvas');
				canvas.width = Math.max(1, Math.round(image.width * scale));
				canvas.height = Math.max(1, Math.round(image.height * scale));

				const ctx = canvas.getContext('2d');
				if (!ctx) return resolve(null);
				ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

				resolve({
					dataUrl: canvas.toDataURL('image/jpeg', 0.8),
					format: 'JPEG',
					width: canvas.width,
					height: canvas.height,
					title,
					caption,
				});
			} catch {
				// A tainted canvas (missing CORS headers on the storage bucket)
				// throws here. Skip the image rather than fail the report.
				resolve(null);
			}
		};

		image.onerror = () => resolve(null);
		image.src = url;
	});
}

/* Rating labels + display order come from the shared INSPECTION_RATINGS
 * (Good / Needs Attention / Critical), so a PDF reader sees exactly the
 * vocabulary the inspector tapped. This file used to keep its own copy of
 * the retired four-tier scale (Good / Satisfied / Dissatisfied / N/A), and
 * its Summary line silently left out every Needs Attention and Critical
 * item. */

/**
 * Minimal shape the report needs from a checklist item. Structural rather than
 * a hard dependency on ChecklistItem so the caller can pass either the
 * checklistService or checklistInstanceService representation.
 */
export interface InspectionPdfChecklistItem {
	title: string;
	rating?: string | null;
	/** The inspector's comment for the tier (rating_note). */
	ratingNote?: string | null;
	/** Retired note column — historical rows only. */
	dissatisfiedNote?: string | null;
	details?: string | null;
	sortOrder?: number;
	isRequired?: boolean;
}

/**
 * Generates and downloads a PDF report for an inspection. Includes the
 * customer/building, customer contact, assignment, the inspected areas with
 * their ratings, notes, settings, and a photo URL list (observation
 * + correction photos). Inspections carry no billing or pricing, so the
 * report has no billing or estimate block — those live on the work order
 * report.
 *
 * `checklistItems` carries the rated areas. It is optional only so existing
 * callers keep compiling — pass it whenever the checklist is loaded, otherwise
 * the report omits the results table, which is the single thing reviewers
 * most want to see.
 */
export function generateInspectionPdf(
	inspection: Inspection,
	checklistItems: InspectionPdfChecklistItem[] = [],
	embeddedPhotos: EmbeddablePhoto[] = [],
): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter' });
	const L = new PdfLayout(doc);

	L.header(
		'Inspection Report',
		`${inspection.buildingName} • ${formatDay(inspection.inspectionDate)}`,
	);

	/* --- Overview --- */
	L.section('Overview');
	L.kv('Status', formatLabel(inspection.status));
	L.kv('Inspection Date', formatDay(inspection.inspectionDate));
	if (inspection.completedDate) {
		L.kv('Completed', formatDate(inspection.completedDate));
	}
	L.kv('Priority', inspection.priority ? formatLabel(inspection.priority) : null);
	L.kv('Frequency', inspection.frequency || 'One-time');
	L.spacer();

	/* --- Customer & Building --- */
	L.section('Customer & Building');
	L.kv('Customer', inspection.companyName);
	L.kv('Building', inspection.buildingName);
	L.kv(
		'Project Address',
		formatAddress([inspection.projectAddress, inspection.projectAddressLine2]),
	);
	L.spacer();

	/* --- Customer Contact --- */
	L.section('Customer Contact');
	L.kv('Name', inspection.contactName);
	L.kv('Phone', inspection.contactPhone);
	L.kv('Email', inspection.contactEmail);
	L.spacer();

	/* --- Assignment --- */
	L.section('Assignment');
	L.kv('Inspector', inspection.inspectorName || 'Unassigned');
	L.kv('Primary Assignee (Corrections)', inspection.assigneeName || 'Not yet assigned');
	// Additional assignees — filter out the primary so we don't duplicate
	if (Array.isArray(inspection.assignees) && inspection.assignees.length > 0) {
		const others = inspection.assignees
			.filter((a) => String(a.id) !== String(inspection.assigneeId ?? ''))
			.map((a) => {
				const first = a.firstName ?? a.first_name ?? '';
				const last = a.lastName ?? a.last_name ?? '';
				return `${first} ${last}`.trim() || `User #${a.id}`;
			});
		if (others.length > 0) L.kv('Additional Assignees', others.join(', '));
	}
	if (inspection.startTime || inspection.endTime) {
		const start = inspection.startTime ? new Date(inspection.startTime).toLocaleString() : '—';
		const end = inspection.endTime ? new Date(inspection.endTime).toLocaleString() : '—';
		L.kv('Scheduled Window', `${start} – ${end}`);
	}
	L.spacer();

	/* --- Inspection results ---
	   The areas and their ratings: the substance of the report. Rendered as a
	   table (not key/value rows) so a reviewer can scan the ratings column,
	   with any dissatisfied note carried inline as the reason. */
	if (checklistItems.length > 0) {
		const sorted = [...checklistItems].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
		const ratedCount = sorted.filter((item) => !!item.rating).length;

		L.section(`Inspection Results (${ratedCount} of ${sorted.length} rated)`);

		L.table(
			['Area / Item', 'Rating', 'Notes'],
			sorted.map((item) => [
				item.title,
				ratingLabel(item.rating),
				item.ratingNote || item.dissatisfiedNote || item.details || '—',
			]),
			[3, 1.2, 3],
		);

		/* Rating tally — a one-line summary of how the visit went. */
		const tally = sorted.reduce<Record<string, number>>((acc, item) => {
			if (!item.rating) return acc;
			acc[item.rating] = (acc[item.rating] ?? 0) + 1;
			return acc;
		}, {});
		const tallyLine = INSPECTION_RATINGS
			.filter(({ value }) => tally[value])
			.map(({ value, label }) => `${label}: ${tally[value]}`)
			.join('   ');
		if (tallyLine) {
			L.spacer(2);
			L.kv('Summary', tallyLine);
		}
		L.spacer();
	}

	/* --- Notes --- */
	L.section('Notes');
	L.kv('General Notes', inspection.generalNotes);
	if (inspection.recommendationsRemarks) {
		L.kv('Recommendations', inspection.recommendationsRemarks);
	}
	if (inspection.followUpNotes) {
		L.kv('Follow-Up', inspection.followUpNotes);
	}
	L.spacer();

	/* No Settings block: its only line was "Signature Required", and
	   inspections are never signed (only work orders are). */

	/* --- Photos ---
	   The images themselves when the caller loaded them (see
	   generateInspectionPdfWithPhotos), a captioned list otherwise. A report
	   of an inspection is far more use to a property manager with the
	   evidence in it than with a list of filenames. */
	if (embeddedPhotos.length > 0) {
		L.section(`Photos (${embeddedPhotos.length})`);
		L.photoGrid(embeddedPhotos);
		L.spacer();
	} else if (inspection.photos && inspection.photos.length > 0) {
		L.section(`Photos (${inspection.photos.length})`);
		inspection.photos.forEach((photo, idx) => {
			const label = [photo.title, photo.area].filter(Boolean).join(' — ');
			L.kv(`Photo ${idx + 1}`, label || photo.caption || '(no caption)');
		});
		L.spacer();
	}

	/* --- Footer --- */
	L.section('Record');
	L.kv('Created', formatDateTime(inspection.createdAt));
	L.kv('Updated', formatDateTime(inspection.updatedAt));

	L.footer();
	doc.save(`inspection-${slugify(inspection.buildingName)}-${inspection.id}.pdf`);
}

/**
 * Inspection report WITH the photos embedded.
 *
 * Async because every image has to be fetched and downscaled before jsPDF can
 * draw it. Photos that fail to load are dropped silently — a broken image URL
 * should cost you that photo, not the whole report — so the caller always gets
 * a document.
 *
 * Photos are ordered by area then sort order, so the report reads as a walk
 * through the building rather than upload order.
 */
export async function generateInspectionPdfWithPhotos(
	inspection: Inspection,
	checklistItems: InspectionPdfChecklistItem[] = [],
): Promise<void> {
	const photos = [...(inspection.photos ?? [])].sort((a, b) => {
		const areaCompare = (a.area ?? '').localeCompare(b.area ?? '');
		if (areaCompare !== 0) return areaCompare;
		return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
	});

	const loaded = await Promise.all(
		photos.map((photo) => loadPhotoForEmbed(
			/* Payload shape varies by endpoint — photoUrl on newer responses,
			   url on older ones. */
			photo.photoUrl ?? photo.url,
			// Area first — it is what orients the reader in the building.
			[photo.area, photo.title].filter(Boolean).join(' — '),
			photo.caption ?? '',
		)),
	);

	generateInspectionPdf(
		inspection,
		checklistItems,
		loaded.filter((p): p is EmbeddablePhoto => p !== null),
	);
}

/* ============================================
 * WORK ORDER REPORT
 * ============================================ */

/**
 * Generates and downloads a PDF report for a work order. Covers project
 * info, location/contact, billing, photo counts, time logs, and the
 * signature status.
 */
export function generateWorkOrderPdf(workOrder: WorkOrder): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter' });
	const L = new PdfLayout(doc);

	L.header(
		'Work Order Report',
		`${workOrder.projectName} • ${formatDate(workOrder.scheduledDate)}`,
	);

	/* --- Overview --- */
	L.section('Overview');
	L.kv('Project Name', workOrder.projectName);
	L.kv('Status', formatLabel(workOrder.status));
	L.kv('Priority', formatLabel(workOrder.priority));
	L.kv('Scheduled Date', formatDate(workOrder.scheduledDate));
	L.kv('Business Line', formatLabel(workOrder.businessLine));
	if (workOrder.subType) {
		L.kv('Sub Type', formatLabel(workOrder.subType));
	}
	L.spacer();

	/* --- Description --- */
	L.section('Project Description');
	L.kv('Description', workOrder.description);
	L.spacer();

	/* --- Location & Contact --- */
	L.section('Location & Contact');
	L.kv('Customer', workOrder.companyName);
	L.kv('Location', workOrder.locationName);
	L.kv('Project Address', workOrder.projectAddress);
	L.kv('Assignee', workOrder.assigneeName || 'Unassigned');
	if (workOrder.assigneeRole === 'subcontractor') {
		L.kv('Assignee Role', 'Subcontractor');
	}
	L.kv('Contact Name', workOrder.contactName);
	L.kv('Contact Phone', workOrder.contactPhone);
	L.kv('Contact Email', workOrder.contactEmail);
	if (workOrder.startTime || workOrder.endTime) {
		const start = workOrder.startTime ? new Date(workOrder.startTime).toLocaleString() : '—';
		const end = workOrder.endTime ? new Date(workOrder.endTime).toLocaleString() : '—';
		L.kv('Scheduled Window', `${start} – ${end}`);
	}
	L.spacer();

	/* --- Hours: estimate versus actual --- */
	/* The bucket is what the office planned; the crew's per-person hours
	   are what the job took, confirmed at hand-off. Printed on any work
	   order that carries either figure. */
	const crewHours = (workOrder.assignees ?? [])
		.map((a) => ({ name: formatPersonName(a) ?? `User #${a.id}`, hours: Number(a.pivot?.allocatedHours ?? 0) }))
		.filter((row) => Number.isFinite(row.hours) && row.hours > 0);
	const actualHours = crewHours.reduce((sum, row) => sum + row.hours, 0);
	const estimatedHours = Number(workOrder.allocatedHours ?? 0);
	if (actualHours > 0 || estimatedHours > 0) {
		L.section('Hours');
		if (estimatedHours > 0) L.kv('Estimated', formatHours(estimatedHours));
		L.kv('Actual', actualHours > 0 ? formatHours(actualHours) : 'Not recorded');
		if (estimatedHours > 0 && actualHours > 0) {
			const variance = Number((actualHours - estimatedHours).toFixed(2));
			L.kv('Variance', variance === 0 ? 'On estimate' : variance > 0 ? `Over by ${formatHours(variance)}` : `Under by ${formatHours(Math.abs(variance))}`);
		}
		crewHours.forEach((row) => L.kv(row.name, formatHours(row.hours)));
		L.spacer();
	}

	/* --- Billing --- */
	L.section('Billing');
	L.kv('Billing Type', formatLabel(workOrder.billingType));
	if (workOrder.billingAmount !== null && workOrder.billingAmount !== undefined) {
		L.kv('Billing Amount', `$${workOrder.billingAmount}`);
	}
	if (workOrder.billingInfo && workOrder.billingInfo.length > 0) {
		L.kv('Billing Info', workOrder.billingInfo.join(', '));
	}
	L.kv('Billing Name', workOrder.billingName);
	L.kv('Billing Address', workOrder.billingAddress);
	L.kv('Billing Notes', workOrder.billingNotes);
	L.spacer();

	/* --- Estimate --- */
	if (workOrder.estimatedCost || workOrder.estimateMin || workOrder.estimateMax) {
		L.section('Estimate');
		if (workOrder.estimatedCost) {
			L.kv('Estimated Cost', `$${workOrder.estimatedCost}`);
		}
		if (workOrder.estimateMin || workOrder.estimateMax) {
			L.kv('Estimate Range', `$${workOrder.estimateMin ?? '0'} – $${workOrder.estimateMax ?? '0'}`);
		}
		L.kv(
			'Approved',
			workOrder.estimateApprovedAt
				? `Yes (${formatDate(workOrder.estimateApprovedAt)})`
				: 'No',
		);
		L.spacer();
	}

	/* --- Settings --- */
	L.section('Settings');
	L.kv('Signature Required', workOrder.signatureRequired ? 'Yes' : 'No');
	L.kv('Signature Captured', workOrder.signatureData ? 'Yes' : 'No');
	L.spacer();

	/* --- Photos --- */
	if (workOrder.photos && workOrder.photos.length > 0) {
		L.section(`Photos (${workOrder.photos.length})`);
		const before = workOrder.photos.filter((p) => p.photoType === 'before');
		const after = workOrder.photos.filter((p) => p.photoType === 'after');
		L.kv('Before Photos', String(before.length));
		L.kv('After Photos', String(after.length));
		workOrder.photos.forEach((photo, idx) => {
			const tag = photo.photoType === 'before' ? 'Before' : 'After';
			L.kv(`Photo ${idx + 1}`, `${tag} — ${photo.caption || '(no caption)'}`);
		});
		L.spacer();
	}

	/* --- Time Logs --- */
	if (workOrder.timeLogs && workOrder.timeLogs.length > 0) {
		L.section(`Time Logs (${workOrder.timeLogs.length})`);
		const totalHours = workOrder.timeLogs.reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
		L.kv('Total Hours', formatHours(totalHours));
		workOrder.timeLogs.forEach((log) => {
			L.kv(
				`${log.employeeName} • ${formatDate(log.date)}`,
				`${formatHours(log.hours)}${log.notes ? ` — ${log.notes}` : ''}`,
			);
		});
		L.spacer();
	}

	/* --- Record --- */
	L.section('Record');
	L.kv('Created', formatDateTime(workOrder.createdAt));
	L.kv('Updated', formatDateTime(workOrder.updatedAt));

	L.footer();
	doc.save(`work-order-${slugify(workOrder.projectName)}-${workOrder.id}.pdf`);
}

/* ============================================
 * PROJECT WORK ORDER REPORT
 * ============================================ */

/**
 * Generates and downloads a PDF report for a Project Work Order. Includes
 * the hour pool readout, building/billing details, and a table-style
 * breakdown of each child work order with its allocated hours.
 */
export function generateProjectWorkOrderPdf(project: ProjectWorkOrder): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter' });
	const L = new PdfLayout(doc);

	L.header(
		'Project Work Order Report',
		`${project.name} • ${formatLabel(project.status)}`,
	);

	/* --- Overview --- */
	L.section('Overview');
	L.kv('Project Name', project.name);
	L.kv('Status', formatLabel(project.status));
	L.kv('Description', project.description);
	/* Project-level business line — the one every child WO inherits.
	   Same formatLabel treatment the work order PDF gives the field. */
	if (project.businessLine) L.kv('Business Line', formatLabel(project.businessLine));
	L.kv('Frequency', project.frequency || 'One-time');
	if (project.startDate) L.kv('Start Date', formatDate(project.startDate));
	if (project.endDate) L.kv('End Date', formatDate(project.endDate));
	L.spacer();

	/* --- Building --- */
	if (project.location) {
		L.section('Building');
		L.kv('Name', project.location.name);
		L.kv(
			'Address',
			formatAddress([
				project.location.address,
				project.location.city,
				project.location.state,
				project.location.zip,
			]),
		);
		L.spacer();
	}

	/* --- Hour Pool --- */
	// Sum allocations off the eager-loaded child WOs so the report reflects
	// the same totals shown on the detail page (no extra fetch needed).
	const children = project.workOrders ?? [];
	const totalHours = Number(project.totalHours ?? 0);
	const allocated = children.reduce((sum, wo) => sum + (Number(wo.allocatedHours) || 0), 0);
	const remaining = totalHours - allocated;
	L.section('Project Hours');
	L.kv('Total', formatHours(totalHours));
	L.kv('Allocated', formatHours(allocated));
	L.kv('Remaining', formatHours(remaining));
	L.spacer();

	/* --- Billing --- */
	const hasBilling =
		(project.billingInfo && project.billingInfo.length > 0) || project.billingNotes;
	if (hasBilling) {
		L.section('Billing');
		if (project.billingInfo && project.billingInfo.length > 0) {
			L.kv('Billing Info', project.billingInfo.join(', '));
		}
		L.kv('Billing Notes', project.billingNotes);
		L.spacer();
	}

	/* --- Notes --- */
	if (project.notes) {
		L.section('Notes');
		L.kv('Notes', project.notes);
		L.spacer();
	}

	/* --- Child Work Orders --- */
	L.section(`Work Orders (${children.length})`);
	if (children.length === 0) {
		L.kv('Work Orders', 'No child work orders attached yet.');
	} else {
		children.forEach((wo, idx) => {
			const assignee = wo.assignee
				? `${wo.assignee.firstName ?? ''} ${wo.assignee.lastName ?? ''}`.trim()
				: '—';
			const parts = [
				formatLabel(wo.status),
				`Assignee: ${assignee}`,
				`Hours: ${formatHours(wo.allocatedHours)}`,
				wo.scheduledDate ? `Scheduled: ${formatDate(wo.scheduledDate)}` : null,
			].filter(Boolean);
			L.kv(`${idx + 1}. ${wo.projectName}`, parts.join(' • '));
		});
	}
	L.spacer();

	/* --- Record --- */
	L.section('Record');
	L.kv('Created', formatDateTime(project.createdAt));
	L.kv('Updated', formatDateTime(project.updatedAt));

	L.footer();
	doc.save(`project-work-order-${slugify(project.name)}-${project.id}.pdf`);
}

/* ============================================
 * LIST REPORTS
 * ============================================ */

/**
 * Describes one row of the optional "Active Filters" summary block that
 * sits above every list report. Pages collect their active filter chips
 * and pass them through so the printed report mirrors what the user is
 * looking at on screen.
 */
export interface ListFilterSummary {
	label: string;
	value: string;
}

/**
 * Shared opening block for every list report: brand header, count line,
 * and (when any are active) a small filter recap so a printed copy is
 * self-explanatory once it leaves the user's screen.
 */
function renderListHeader(
	L: PdfLayout,
	title: string,
	count: number,
	filters?: ListFilterSummary[],
) {
	L.header(title, `${count} record${count === 1 ? '' : 's'} • Generated ${formatDate(new Date().toISOString())}`);
	const active = (filters ?? []).filter((f) => f.value && f.value !== '—');
	if (active.length > 0) {
		L.section('Active Filters');
		active.forEach((f) => L.kv(f.label, f.value));
		L.spacer();
	}
}

/**
 * Generates a summary PDF for a list of inspections. Renders one row per
 * record with the building, customer, status, inspector, assignee, and
 * inspection date — enough context to act on each row at a glance.
 */
export function generateInspectionListPdf(
	inspections: Inspection[],
	filters?: ListFilterSummary[],
): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
	const L = new PdfLayout(doc);
	renderListHeader(L, 'Inspections Report', inspections.length, filters);

	L.section('Inspections');
	const rows = inspections.map((i) => [
		i.buildingName || '—',
		i.companyName || '—',
		formatLabel(i.status),
		i.inspectorName || 'Unassigned',
		i.assigneeName || '—',
		formatDay(i.inspectionDate),
	]);
	L.table(
		['Building', 'Customer', 'Status', 'Inspector', 'Assignee', 'Date'],
		rows,
		[3, 3, 2, 2.5, 2.5, 2],
	);

	L.footer();
	doc.save(`inspections-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Generates a summary PDF for a list of work orders. Renders one row per
 * record with the project name, customer/location, status, priority,
 * assignee, and scheduled date.
 */
export function generateWorkOrderListPdf(
	workOrders: WorkOrder[],
	filters?: ListFilterSummary[],
): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
	const L = new PdfLayout(doc);
	renderListHeader(L, 'Work Orders Report', workOrders.length, filters);

	L.section('Work Orders');
	const rows = workOrders.map((wo) => [
		wo.projectName || '—',
		wo.companyName || wo.locationName || '—',
		formatLabel(wo.status),
		formatLabel(wo.priority),
		wo.assigneeName || 'Unassigned',
		formatDate(wo.scheduledDate),
	]);
	L.table(
		['Project', 'Customer / Location', 'Status', 'Priority', 'Assignee', 'Scheduled'],
		rows,
		[3, 3, 2, 1.5, 2.5, 2],
	);

	L.footer();
	doc.save(`work-orders-${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Generates a summary PDF for a list of Project Work Orders. Renders one
 * row per project with status, hour pool (allocated/total/remaining),
 * and schedule window — the same fields shown on the list table.
 */
export function generateProjectWorkOrderListPdf(
	projects: ProjectWorkOrder[],
	filters?: ListFilterSummary[],
): void {
	const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
	const L = new PdfLayout(doc);
	renderListHeader(L, 'Project Work Orders Report', projects.length, filters);

	L.section('Projects');
	const rows = projects.map((p) => {
		const allocated = Number(p.hoursAllocated ?? 0);
		const total = Number(p.totalHours ?? 0);
		const remaining = total - allocated;
		const schedule =
			(p.startDate ? formatDate(p.startDate) : '—') +
			(p.endDate ? ` – ${formatDate(p.endDate)}` : '');
		return [
			p.name || '—',
			formatLabel(p.status),
			`${allocated} / ${total}`,
			String(remaining),
			schedule,
			formatDate(p.updatedAt),
		];
	});
	L.table(
		['Project', 'Status', 'Allocated / Total', 'Remaining', 'Schedule', 'Updated'],
		rows,
		[3.5, 1.5, 2, 1.5, 2.5, 2],
	);

	L.footer();
	doc.save(`project-work-orders-${new Date().toISOString().slice(0, 10)}.pdf`);
}
